import { Link } from 'react-router-dom';
import { Building2, Clock, GripVertical, MapPin, RefreshCcw } from 'lucide-react';
import type { Task } from '@4zen/api-client';
import { PriorityDot, StatusBadge, cn } from '@4zen/ui';
import { formatDuration, formatTime } from '@/lib/format';

interface Props {
  task: Task;
  compact?: boolean;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  trailing?: React.ReactNode;
}

export function TaskCard({
  task,
  compact = false,
  draggable = false,
  onDragStart,
  onDragEnd,
  trailing,
}: Props) {
  const closed = task.status === 'done' || task.status === 'not_done';

  return (
    <article
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'group rounded border border-line bg-surface transition-colors',
        draggable && 'cursor-grab active:cursor-grabbing',
        closed && 'opacity-60',
        task.is_voided && 'zen-voided',
        compact ? 'p-2' : 'p-3',
      )}
    >
      <div className="flex items-start gap-2">
        {draggable ? (
          <GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-subtle opacity-0 transition-opacity group-hover:opacity-100" />
        ) : null}
        <PriorityDot priority={task.priority} className="mt-1.5 shrink-0" />

        <div className="min-w-0 flex-1">
          <Link
            to={`/tasks/${task.id}`}
            className={cn(
              'block truncate text-sm hover:text-accent',
              closed && 'line-through',
              !compact && 'font-medium',
            )}
          >
            {task.title}
          </Link>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-content-subtle">
            {task.scheduled_start ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatTime(task.scheduled_start)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(task.estimate_minutes)}
              </span>
            )}
            {task.client_name ? (
              <span className="inline-flex min-w-0 items-center gap-1">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{task.client_name}</span>
              </span>
            ) : null}
            {task.location ? (
              <span className="inline-flex min-w-0 items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{task.location}</span>
              </span>
            ) : null}
            {task.reschedule_count > 0 ? (
              <span
                className="inline-flex items-center gap-1 text-warning"
                title={`Переносилась ${task.reschedule_count} раз(и)`}
              >
                <RefreshCcw className="h-3 w-3" />
                {task.reschedule_count}
              </span>
            ) : null}
            {task.checklist.length > 0 ? (
              <span>
                {task.checklist.filter((item) => item.is_done).length}/{task.checklist.length}
              </span>
            ) : null}
          </div>
        </div>

        {!compact ? <StatusBadge status={task.status} /> : null}
        {trailing}
      </div>
    </article>
  );
}
